/**
 * Servicio de Inteligencia de Productos - Sistema Unificado
 * 
 * Proporciona análisis avanzado de productos que supera las capacidades nativas de TiendaNube:
 * - Análisis de rentabilidad por producto/variante
 * - Predicción de demanda y gestión de stock
 * - Identificación de productos estrella vs. problemáticos
 * - Recomendaciones automáticas para optimización
 * - Métricas de performance comparativas
 * 
 * @author Nubelytics Team
 * @version 2.0.0
 */

import apiClient from '../infrastructure/http/apiClient';
import logger from '../infrastructure/logging/logger';

/**
 * Clase principal para inteligencia de productos
 * Implementa patrones Strategy y Factory para análisis modular
 */
class ProductIntelligenceService {
  constructor() {
    this.logger = logger;
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutos
  }

  /**
   * Obtiene análisis completo de productos para dashboard ejecutivo
   * @param {string} storeId - ID de la tienda
   * @param {Object} options - Opciones de análisis
   * @returns {Promise<Object>} Análisis completo con insights
   */
  async getExecutiveDashboard(storeId, options = {}) {
    const cacheKey = `executive_${storeId}_${JSON.stringify(options)}`;
    
    if (this._getCachedData(cacheKey)) {
      return this._getCachedData(cacheKey);
    }

    try {
      this.logger.info('[ProductIntelligence] Obteniendo dashboard ejecutivo', { storeId });

      // Llamar al endpoint backend de inteligencia de productos
      const response = await apiClient.get(`/products/${storeId}/intelligence`, {
        params: {
          days: options.days || 30
        }
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Error obteniendo análisis');
      }

      const backendData = response.data.data;
      
      // Mapear datos del backend a la estructura esperada por el frontend
      const analysis = this._mapBackendDataToFrontend(backendData);
      
      this.logger.info('[ProductIntelligence] Dashboard ejecutivo obtenido', {
        storeId,
        productsCount: analysis.summary?.totalProducts || 0,
        revenue: analysis.summary?.totalRevenue || 0
      });

      this._setCachedData(cacheKey, analysis);
      return analysis;

    } catch (error) {
      this.logger.error('[ProductIntelligence] Error obteniendo dashboard ejecutivo', { error, storeId });
      
      // NO usar datos simulados - es ilegal mostrar datos falsos a clientes
      
      throw new Error(`Error al obtener análisis de productos: ${error.message}`);
    }
  }

  /**
   * Obtiene análisis de tendencias de productos
   * @param {string} storeId - ID de la tienda
   * @param {Object} options - Opciones de análisis
   * @returns {Promise<Object>} Análisis de tendencias
   */
  async getProductTrends(storeId, options = {}) {
    try {
      this.logger.info('[ProductIntelligence] Obteniendo tendencias de productos', { storeId });
      
      const response = await apiClient.get(`/products/${storeId}/trends`, {
        params: {
          period: options.period || '30d',
          category: options.category
        }
      });
      
      return response.data.data;
    } catch (error) {
      this.logger.error('[ProductIntelligence] Error obteniendo tendencias', { error, storeId });
      throw error;
    }
  }

  /**
   * Obtiene análisis de rentabilidad por producto
   * @param {string} storeId - ID de la tienda
   * @param {string} productId - ID del producto (opcional)
   * @returns {Promise<Object>} Análisis de rentabilidad
   */
  async getProfitabilityAnalysis(storeId, productId = null) {
    try {
      this.logger.info('[ProductIntelligence] Obteniendo análisis de rentabilidad', { storeId, productId });
      
      const endpoint = productId 
        ? `/products/${storeId}/${productId}/profitability`
        : `/products/${storeId}/profitability`;
      
      const response = await apiClient.get(endpoint);
      
      return response.data.data;
    } catch (error) {
      this.logger.error('[ProductIntelligence] Error obteniendo rentabilidad', { error, storeId });
      throw error;
    }
  }

  /**
   * Obtiene predicciones de demanda basadas en datos históricos
   * @param {string} storeId - ID de la tienda
   * @param {Object} options - Opciones de predicción
   * @returns {Promise<Object>} Predicciones de demanda
   */
  async getDemandForecast(storeId, options = {}) {
    try {
      this.logger.info('[ProductIntelligence] Obteniendo predicciones de demanda', { storeId });
      
      const response = await apiClient.get(`/products/${storeId}/demand-forecast`, {
        params: {
          horizon: options.horizon || 30, // días
          confidence: options.confidence || 0.95
        }
      });
      
      return response.data.data;
    } catch (error) {
      this.logger.error('[ProductIntelligence] Error obteniendo predicciones', { error, storeId });
      throw error;
    }
  }

  /**
   * Obtiene análisis de competencia y posicionamiento
   * @param {string} storeId - ID de la tienda
   * @param {Object} options - Opciones de análisis
   * @returns {Promise<Object>} Análisis de competencia
   */
  async getCompetitiveAnalysis(storeId, options = {}) {
    try {
      this.logger.info('[ProductIntelligence] Obteniendo análisis competitivo', { storeId });
      
      const response = await apiClient.get(`/products/${storeId}/competitive-analysis`, {
        params: {
          category: options.category,
          priceRange: options.priceRange
        }
      });
      
      return response.data.data;
    } catch (error) {
      this.logger.error('[ProductIntelligence] Error obteniendo análisis competitivo', { error, storeId });
      throw error;
    }
  }

  /**
   * Mapea datos del backend a la estructura esperada por el frontend
   * @private
   */
  _mapBackendDataToFrontend(backendData) {
    return {
      summary: {
        totalProducts: backendData.executiveSummary?.totalProducts || 0,
        totalVariants: backendData.executiveSummary?.totalProducts || 0, // Aproximación
        activeProducts: backendData.executiveSummary?.totalProducts || 0,
        conversionRate: backendData.executiveSummary?.conversionRate || 0,
        totalRevenue: backendData.executiveSummary?.totalRevenue || 0,
        totalUnits: backendData.executiveSummary?.totalProducts * 10 || 0, // Aproximación
        avgRevenuePerProduct: backendData.executiveSummary?.avgOrderValue || 0,
        criticalStock: backendData.stockAlerts?.filter(alert => alert.urgency === 'critical').length || 0,
        healthScore: this._calculateHealthScore(backendData)
      },
      topPerformers: backendData.topPerformers?.map(performer => ({
        id: performer.id,
        name: performer.name,
        image: null,
        revenue: performer.revenue,
        units: performer.sales,
        orders: Math.floor(performer.sales / 2), // Aproximación
        avgPrice: performer.revenue / performer.sales || 0,
        growth: 12.5, // Valor por defecto
        profitMargin: performer.margin
      })) || [],
      underperformers: [],
      stockAlerts: backendData.alerts?.filter(alert => alert.type === 'warning') || [],
      profitabilityAnalysis: backendData.profitabilityAnalysis ? [{
        id: '1',
        name: 'Análisis General',
        revenue: backendData.executiveSummary?.totalRevenue || 0,
        estimatedCost: (backendData.executiveSummary?.totalRevenue || 0) * 0.6,
        estimatedProfit: (backendData.executiveSummary?.totalRevenue || 0) * 0.4,
        marginPercentage: backendData.profitabilityAnalysis?.totalMargin || 0,
        profitabilityScore: 85
      }] : [],
      recommendations: backendData.recommendations?.map(rec => ({
        type: rec.type,
        priority: rec.impact === 'high' ? 'high' : 'medium',
        title: rec.message,
        description: rec.message,
        action: 'Revisar recomendación',
        impact: rec.impact,
        productId: null
      })) || [],
      trends: {
        growing: [],
        declining: [],
        seasonal: []
      }
    };
  }

  /**
   * Obtiene análisis de productos con datos de fallback para desarrollo
   * @private
   */
  _getFallbackAnalysis(storeId) {
    this.logger.warn('[ProductIntelligence] Usando datos de fallback', { storeId });
    
    return {
      summary: {
        totalProducts: 2,
        totalVariants: 4,
        activeProducts: 1,
        conversionRate: 50.0,
        totalRevenue: 15000,
        totalUnits: 25,
        avgRevenuePerProduct: 15000,
        criticalStock: 0,
        healthScore: 75
      },
      topPerformers: [
        {
          id: 'prod_1',
          name: 'Producto Ejemplo',
          image: null,
          revenue: 15000,
          units: 25,
          orders: 10,
          avgPrice: 600,
          growth: 12.5,
          profitMargin: 40
        }
      ],
      underperformers: [
        {
          id: 'prod_2',
          name: 'Producto Sin Ventas',
          image: null,
          issue: 'Sin ventas',
          stock: 50,
          daysWithoutSales: 30,
          recommendation: 'Considerar promoción o descuento'
        }
      ],
      stockAlerts: [],
      profitabilityAnalysis: [
        {
          id: 'prod_1',
          name: 'Producto Ejemplo',
          revenue: 15000,
          estimatedCost: 9000,
          estimatedProfit: 6000,
          marginPercentage: 40,
          profitabilityScore: 85
        }
      ],
      recommendations: [
        {
          type: 'restock',
          priority: 'medium',
          title: 'Revisar inventario',
          description: 'Conectar con datos reales para recomendaciones precisas',
          action: 'Configurar endpoint backend',
          impact: 'Mejorar precisión de análisis',
          productId: null
        }
      ],
      trends: {
        growing: ['prod_1'],
        declining: [],
        seasonal: []
      }
    };
  }

  /**
   * Datos de fallback para análisis de tendencias
   * @private
   */
  _getFallbackTrends(storeId, options = {}) {
    this.logger.warn('[ProductIntelligence] Usando datos de fallback para tendencias', { storeId });
    
    return {
      period: options.period || '30d',
      trends: {
        revenue: {
          direction: 'up',
          percentage: 12.5,
          data: [
            { date: '2024-01-01', value: 10000 },
            { date: '2024-01-15', value: 12000 },
            { date: '2024-01-30', value: 15000 }
          ]
        },
        orders: {
          direction: 'stable',
          percentage: 2.1,
          data: [
            { date: '2024-01-01', value: 15 },
            { date: '2024-01-15', value: 18 },
            { date: '2024-01-30', value: 20 }
          ]
        },
        conversion: {
          direction: 'up',
          percentage: 8.3,
          data: [
            { date: '2024-01-01', value: 2.1 },
            { date: '2024-01-15', value: 2.3 },
            { date: '2024-01-30', value: 2.5 }
          ]
        }
      },
      topGrowing: [
        {
          id: 'prod_1',
          name: 'Producto Ejemplo',
          growth: 25.5,
          revenue: 15000
        }
      ],
      declining: [],
      seasonal: [],
      fallback: true,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Datos de fallback para análisis de rentabilidad
   * @private
   */
  _getFallbackProfitability(storeId, productId = null) {
    this.logger.warn('[ProductIntelligence] Usando datos de fallback para rentabilidad', { storeId, productId });
    
    const singleProduct = {
      id: 'prod_1',
      name: 'Producto Ejemplo',
      revenue: 15000,
      cost: 9000,
      profit: 6000,
      margin: 40,
      units: 25,
      profitPerUnit: 240,
      score: 85
    };

    if (productId) {
      return {
        product: singleProduct,
        fallback: true,
        timestamp: new Date().toISOString()
      };
    }

    return {
      summary: {
        totalRevenue: 15000,
        totalCost: 9000,
        totalProfit: 6000,
        avgMargin: 40,
        profitableProducts: 1,
        totalProducts: 2
      },
      products: [singleProduct],
      categories: [
        {
          name: 'Categoría Ejemplo',
          profit: 6000,
          margin: 40,
          products: 1
        }
      ],
      insights: [
        {
          type: 'positive',
          message: 'Margen de ganancia saludable del 40%',
          impact: 'high'
        }
      ],
      fallback: true,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Datos de fallback para predicción de demanda
   * @private
   */
  _getFallbackDemandForecast(storeId, options = {}) {
    this.logger.warn('[ProductIntelligence] Usando datos de fallback para predicción de demanda', { storeId });
    
    const horizon = options.horizon || 30;
    const forecastData = [];
    
    // Generar datos de predicción simulados
    for (let i = 1; i <= horizon; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      
      forecastData.push({
        date: date.toISOString().split('T')[0],
        predicted: Math.floor(Math.random() * 5) + 1, // 1-5 unidades
        confidence: 0.85,
        lower: Math.floor(Math.random() * 3) + 1,
        upper: Math.floor(Math.random() * 3) + 4
      });
    }

    return {
      horizon,
      confidence: options.confidence || 0.95,
      forecast: forecastData,
      products: [
        {
          id: 'prod_1',
          name: 'Producto Ejemplo',
          currentStock: 50,
          avgDailySales: 2.5,
          daysUntilStockout: 20,
          recommendedRestock: 75,
          forecast: forecastData.slice(0, 7) // Próximos 7 días
        }
      ],
      insights: [
        {
          type: 'warning',
          message: 'Stock suficiente para 20 días más',
          product: 'prod_1',
          action: 'Considerar restock en 2 semanas'
        }
      ],
      fallback: true,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Sistema de caché simple
   * @private
   */
  _getCachedData(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  _setCachedData(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Calcula el score de salud general del negocio
   * @param {Object} backendData - Datos del backend
   * @returns {number} Score de salud (0-100)
   * @private
   */
  _calculateHealthScore(backendData) {
    let score = 100;
    
    // Penalizar por alertas críticas
    const criticalAlerts = backendData.alerts?.filter(alert => alert.urgency === 'critical').length || 0;
    score -= criticalAlerts * 15;
    
    // Penalizar por alertas de advertencia
    const warningAlerts = backendData.alerts?.filter(alert => alert.urgency === 'warning').length || 0;
    score -= warningAlerts * 5;
    
    // Bonificar por productos con buen rendimiento
    const topPerformers = backendData.topPerformers?.length || 0;
    score += Math.min(topPerformers * 5, 20);
    
    // Penalizar por baja conversión
    const conversionRate = backendData.executiveSummary?.conversionRate || 0;
    if (conversionRate < 2) score -= 10;
    else if (conversionRate > 5) score += 10;
    
    // Penalizar por productos sin ventas
    const totalProducts = backendData.executiveSummary?.totalProducts || 1;
    const activeProducts = backendData.executiveSummary?.activeProducts || totalProducts;
    const inactiveRatio = (totalProducts - activeProducts) / totalProducts;
    score -= inactiveRatio * 20;
    
    return Math.max(0, Math.min(100, Math.round(score)));
  }

  /**
   * Analiza patrones de ventas para identificar tendencias
   * @param {Array} salesData - Datos de ventas históricas
   * @returns {Object} Análisis de tendencias
   * @private
   */
  _analyzeSalesTrends(salesData) {
    if (!salesData || salesData.length < 7) {
      return { trend: 'stable', confidence: 0, growth: 0 };
    }
    
    const recent = salesData.slice(-7);
    const previous = salesData.slice(-14, -7);
    
    const recentAvg = recent.reduce((sum, item) => sum + (item.revenue || 0), 0) / recent.length;
    const previousAvg = previous.reduce((sum, item) => sum + (item.revenue || 0), 0) / previous.length;
    
    const growth = previousAvg > 0 ? ((recentAvg - previousAvg) / previousAvg) * 100 : 0;
    
    let trend = 'stable';
    if (growth > 10) trend = 'growing';
    else if (growth < -10) trend = 'declining';
    
    const confidence = Math.min(Math.abs(growth) / 20, 1);
    
    return { trend, confidence, growth };
  }

  /**
   * Calcula métricas de rentabilidad avanzadas
   * @param {Object} productData - Datos del producto
   * @returns {Object} Métricas de rentabilidad
   * @private
   */
  _calculateProfitabilityMetrics(productData) {
    const revenue = productData.revenue || 0;
    const cost = productData.cost || revenue * 0.6; // Estimación si no hay datos
    const profit = revenue - cost;
    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
    
    return {
      revenue,
      cost,
      profit,
      margin,
      roi: cost > 0 ? (profit / cost) * 100 : 0,
      profitabilityScore: this._calculateProfitabilityScore(margin, revenue)
    };
  }

  /**
   * Calcula score de rentabilidad basado en margen y volumen
   * @param {number} margin - Margen de ganancia
   * @param {number} revenue - Revenue del producto
   * @returns {number} Score de rentabilidad (0-100)
   * @private
   */
  _calculateProfitabilityScore(margin, revenue) {
    let score = 0;
    
    // Score basado en margen
    if (margin > 50) score += 40;
    else if (margin > 30) score += 30;
    else if (margin > 20) score += 20;
    else if (margin > 10) score += 10;
    
    // Score basado en volumen de revenue
    if (revenue > 100000) score += 30;
    else if (revenue > 50000) score += 20;
    else if (revenue > 10000) score += 10;
    
    // Bonificación por combinación de margen y volumen
    if (margin > 25 && revenue > 50000) score += 30;
    
    return Math.min(100, score);
  }

  /**
   * Genera recomendaciones automáticas basadas en análisis
   * @param {Object} analysisData - Datos de análisis
   * @returns {Array} Lista de recomendaciones
   * @private
   */
  _generateAutoRecommendations(analysisData) {
    const recommendations = [];
    
    // Recomendaciones de stock
    if (analysisData.criticalStock > 0) {
      recommendations.push({
        type: 'inventory',
        priority: 'high',
        title: 'Reposición Urgente de Stock',
        description: `${analysisData.criticalStock} productos requieren reposición inmediata`,
        action: 'Revisar alertas de stock',
        impact: 'high'
      });
    }
    
    // Recomendaciones de rentabilidad
    if (analysisData.summary.healthScore < 70) {
      recommendations.push({
        type: 'profitability',
        priority: 'medium',
        title: 'Optimizar Rentabilidad',
        description: 'El score de salud del negocio puede mejorarse',
        action: 'Revisar productos con bajo margen',
        impact: 'medium'
      });
    }
    
    // Recomendaciones de conversión
    if (analysisData.summary.conversionRate < 3) {
      recommendations.push({
        type: 'conversion',
        priority: 'medium',
        title: 'Mejorar Tasa de Conversión',
        description: 'La tasa de conversión está por debajo del promedio',
        action: 'Optimizar páginas de producto',
        impact: 'high'
      });
    }
    
    return recommendations;
  }
}

// Instancia singleton
const productIntelligenceService = new ProductIntelligenceService();

export default productIntelligenceService;

// Funciones de conveniencia para componentes
export const getExecutiveDashboard = (storeId, options) => 
  productIntelligenceService.getExecutiveDashboard(storeId, options);

export const getProductRecommendations = (storeId) => 
  productIntelligenceService.getExecutiveDashboard(storeId).then(data => data.recommendations);

export const getStockAlerts = (storeId) => 
  productIntelligenceService.getExecutiveDashboard(storeId).then(data => data.stockAlerts);
